import { useEffect, useCallback } from 'react';
import isHotkey from 'is-hotkey';

/**
 * Detects if the user is on Mac (Command key) or Windows/Linux (Control key)
 */
export const isMac = () => {
  return typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
};

/**
 * Returns the appropriate modifier key symbol for the platform
 */
export const getModifierKey = () => {
  return isMac() ? '⌘' : 'Ctrl';
};

/**
 * Returns the appropriate modifier key text for display
 */
export const getModifierKeyText = () => {
  return isMac() ? 'Cmd' : 'Ctrl';
};

/**
 * Keyboard shortcuts configuration
 */
export interface ShortcutConfig {
  key: string;
  description: string;
  category: 'draft' | 'chat' | 'navigation';
  handler: () => void;
  enabledWhenInputFocused?: boolean; // Default: false (disabled in inputs)
}

/**
 * Hook to register keyboard shortcuts globally
 *
 * @example
 * useKeyboardShortcuts([
 *   {
 *     key: 'mod+g',
 *     description: 'Generate draft',
 *     category: 'draft',
 *     handler: () => handleGenerate(),
 *   }
 * ]);
 */
export function useKeyboardShortcuts(shortcuts: ShortcutConfig[]) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Check if we're in an input field
      const target = event.target as HTMLElement;
      const isInInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Process each shortcut
      for (const shortcut of shortcuts) {
        // Skip if in input and shortcut is not enabled for inputs
        if (isInInput && !shortcut.enabledWhenInputFocused) {
          continue;
        }

        // Check if the hotkey matches
        if (isHotkey(shortcut.key, event)) {
          event.preventDefault();
          event.stopPropagation();
          shortcut.handler();
          break;
        }
      }
    },
    [shortcuts]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

/**
 * Format a keyboard shortcut for display
 * Converts 'mod+g' to '⌘G' on Mac or 'Ctrl+G' on Windows
 */
export function formatShortcut(key: string): string {
  const parts = key.split('+');
  const formatted = parts.map((part) => {
    switch (part.toLowerCase()) {
      case 'mod':
        return isMac() ? '⌘' : 'Ctrl';
      case 'shift':
        return isMac() ? '⇧' : 'Shift';
      case 'alt':
        return isMac() ? '⌥' : 'Alt';
      case 'enter':
        return isMac() ? '↵' : 'Enter';
      case 'escape':
        return 'Esc';
      default:
        return part.toUpperCase();
    }
  });

  return formatted.join(isMac() ? '' : '+');
}

/**
 * Get all available keyboard shortcuts grouped by category
 */
export const getAllShortcuts = () => {
  return {
    draft: [
      { key: 'mod+g', description: 'Generate draft' },
      { key: 'mod+r', description: 'Regenerate draft' },
      { key: 'mod+k', description: 'Copy draft to clipboard' },
      { key: 'mod+enter', description: 'Insert draft into Front' },
    ],
    chat: [
      { key: 'mod+/', description: 'Toggle chat interface' },
      { key: 'mod+enter', description: 'Send chat message (when in input)' },
    ],
    navigation: [
      { key: 'escape', description: 'Close modals/dropdowns' },
      { key: '?', description: 'Show keyboard shortcuts help' },
    ],
  };
};
